<!--
 * @Author: qoder
 * @Description: 维修工单列表
-->
<template>
  <scroll-layout class="repair-order-list">
    <template slot="header">
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>
    <ykc-table
      ref="YkcTable"
      operateFixedType="right"
      :selectable="true"
      :data="tableData"
      title="维修工单列表"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons.filter(item => item.enabled())"
      @selection-change="handleSelectionChange">
      <template #headerButtons>
        <div class="table-header-button">
          <ykc-button v-rbac="'operation:repairOrder:create'" type="plain" @click="handleCreate">
            创建维修工单
          </ykc-button>
          <ykc-button v-rbac="'operation:repairOrder:export'" type="plain" @click="doExportFile">
            导出
          </ykc-button>
          <ykc-button type="plain" :disabled="selectedIds.length === 0" @click="handleBatchCancel">
            批量作废
          </ykc-button>
          <ykc-button
            type="plain"
            :disabled="selectedIds.length === 0"
            @click="handleBatchAddRecord">
            批量添加维修记录
          </ykc-button>
          <ykc-button
            type="plain"
            :disabled="selectedIds.length === 0"
            @click="handleBatchComplete">
            批量完成
          </ykc-button>
          <ykc-button
            type="plain"
            :disabled="selectedIds.length === 0"
            @click="handleBatchAddRemark">
            批量添加维修备注
          </ykc-button>
          <ykc-button type="plain" :disabled="selectedIds.length === 0" @click="handleBatchMark(1)">
            批量标记
          </ykc-button>
          <ykc-button type="plain" :disabled="selectedIds.length === 0" @click="handleBatchMark(0)">
            批量取消标记
          </ykc-button>
        </div>
      </template>

      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>

    <!-- 创建/复制工单抽屉 -->
    <ykc-drawer
      :title="drawerTitle"
      :show.sync="showDrawer"
      :on-close="onDrawerClose"
      :before-cancel="onDrawerCancel">
      <template #footer>
        <div class="drawer-button-wrap">
          <template v-for="(btn, i) in drawerButtons">
            <ykc-button :type="btn.type" v-if="btn.enabled()" :key="i" @click="btn.handler">
              {{ btn.label }}
            </ykc-button>
          </template>
        </div>
      </template>
      <div class="drawer-main-body" v-if="showDrawer">
        <CreateOrCopy
          v-if="drawerType === 'create' || drawerType === 'copy'"
          ref="createOrCopy"
          :drawerType="drawerType"
          :copyData="copyData"
          @success="handleCreateSuccess"></CreateOrCopy>
        <AddRecord
          v-if="drawerType === 'record'"
          ref="addRecord"
          :orderInfo="currentOrder"
          :batchOrderIds="batchOrderIds"
          @success="handleRecordSuccess"></AddRecord>
        <CompleteOrRemark
          v-if="drawerType === 'complete' || drawerType === 'remark'"
          ref="completeOrRemark"
          :mode="drawerType === 'remark' ? 'remark' : 'complete'"
          :orderInfo="currentOrder"
          :batchOrderIds="batchOrderIds"
          @success="handleRecordSuccess"></CompleteOrRemark>
      </div>
    </ykc-drawer>

    <!-- 确认弹窗 -->
    <ykc-operation-dialog
      :title="confirmTitle"
      :show.sync="confirmVisible"
      :before-close="onConfirmClose"
      :before-confirm="handleConfirmSubmit"
      :confirm-loading="confirmLoading"
      :confirm-btn-txt="confirmBtnTxt">
      <div slot="content">
        <div class="confirm-modal">
          <div class="confirm-icon" :style="{ background: confirmIconBg }">
            <i :class="['fas', confirmIcon, confirmIconColor]"></i>
          </div>
          <h3>{{ confirmTitle }}</h3>
          <p>{{ confirmText }}</p>
        </div>
      </div>
    </ykc-operation-dialog>
  </scroll-layout>
</template>

<script>
  import YkcDialog from '@/components/ykc-ui/dialog';
  import { repairOrderApi, treeUtils, barrierPage } from '@/service/apis';
  import { code, offlineExport } from '@/utils';
  import CreateOrCopy from './CreateOrCopy.vue';
  import AddRecord from './AddRecord.vue';
  import CompleteOrRemark from './CompleteOrRemark.vue';

  export default {
    name: 'RepairOrderList',
    components: {
      CreateOrCopy,
      AddRecord,
      CompleteOrRemark,
    },
    data() {
      return {
        // 表格数据
        tableData: [],
        // 分页信息
        pageInfo: {
          current: 1,
          size: 10,
          total: 0,
        },
        // 搜索参数
        searchParams: {
          orderNo: '',
          assigneeName: '',
          deviceNo: '',
          stationId: '',
          cityCode: '',
          status: '',
          platform: '',
          creatorName: '',
          faultLevelList: [],
          markStatus: '',
          createTime: [],
          createTimeStart: '',
          createTimeEnd: '',
          finishTime: [],
          finishTimeStart: '',
          finishTimeEnd: '',
        },
        // 城市数据
        addressData: [],
        // 电站数据
        stationIdData: [],
        // 抽屉相关
        showDrawer: false,
        drawerType: 'create',
        currentOrderId: null,
        currentOrder: null,
        copyData: null,
        // 确认弹窗
        confirmVisible: false,
        confirmLoading: false,
        confirmTitle: '',
        confirmText: '',
        confirmType: '', // 'cancel' | 'batchCancel' | 'batchMark'
        confirmBtnTxt: '',
        // 批量操作
        selectedRows: [],
        selectedIds: [],
        batchOrderIds: null,
        batchMarkStatus: null,
      };
    },
    computed: {
      searchData() {
        const all = { id: '', name: '全部' };
        return [
          {
            comName: 'YkcInput',
            key: 'orderNo',
            label: '工单编码',
            placeholder: '请输入工单编码',
          },
          {
            comName: 'YkcDropdown',
            key: 'faultLevelList',
            label: '故障等级',
            multiple: true,
            clearable: true,
            placeholder: '请选择故障等级',
            data: [
              { id: 1, name: 'I级（致命故障）' },
              { id: 2, name: 'II级（中度故障）' },
              { id: 3, name: 'III级（一般故障）' },
              { id: 4, name: 'IV级（轻微告警）' },
            ],
          },
          {
            comName: 'YkcDropdown',
            key: 'markStatus',
            label: '标记状态',
            placeholder: '请选择标记状态',
            data: [all, { id: 0, name: '未标记' }, { id: 1, name: '已标记' }],
          },
          {
            comName: 'YkcInput',
            key: 'assigneeName',
            label: '工单负责人',
            placeholder: '请输入工单负责人',
          },
          {
            comName: 'YkcInput',
            key: 'creatorName',
            label: '创建人',
            placeholder: '请输入创建人',
          },
          {
            comName: 'YkcInput',
            key: 'deviceNo',
            label: '设备编码',
            placeholder: '请输入设备编码',
          },
          {
            comName: 'YkcDropdown',
            key: 'platform',
            label: '所属平台',
            placeholder: '请选择所属平台',
            data: [all, { id: 1, name: '淘淘充' }, { id: 2, name: '益虫' }],
          },
          {
            comName: 'YkcDropdown',
            key: 'stationId',
            label: '所属电站',
            placeholder: '请选择电站',
            data: this.stationIdData,
            filterable: true,
          },
          {
            comName: 'YkcDropdown',
            key: 'status',
            label: '工单状态',
            placeholder: '请选择工单状态',
            data: [
              all,
              { id: 10, name: '待维修' },
              { id: 20, name: '维修中' },
              { id: 30, name: '已结转' },
              { id: 40, name: '已完成' },
              { id: 50, name: '已作废' },
            ],
          },
          {
            comName: 'YkcCascader',
            key: 'cityCode',
            label: '所属城市',
            placeholder: '请选择城市',
            configSet: {
              label: 'name',
              value: 'id',
              children: 'subNodes',
            },
            options: this.addressData,
            filterable: true,
            clearable: true,
            checkStrictly: true,
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            key: 'createTime',
            label: '创建时间',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            key: 'finishTime',
            label: '完成时间',
          },
        ];
      },
      drawerTitle() {
        const titleMap = {
          create: '创建维修工单',
          copy: '复制维修工单',
          record: '添加维修记录',
          complete: '完成工单',
          remark: '添加维修备注',
        };
        return titleMap[this.drawerType] || '维修工单';
      },
      drawerButtons() {
        return [
          {
            label: '取消',
            type: 'plain',
            enabled: () => true,
            handler: () => {
              YkcDialog({
                dialogType: 'feedback',
                showTitle: false,
                showFooter: true,
                desc: '取消后操作将不被保存，确认是否取消？',
                onCancel: done => {
                  done();
                },
                onConfirm: done => {
                  done();
                  this.showDrawer = false;
                },
              });
            },
          },
          {
            label: '确认',
            enabled: () => true,
            handler: async () => {
              if (this.drawerType === 'record') {
                const success = await this.$refs.addRecord?.submit();
                if (success) {
                  this.showDrawer = false;
                }
              } else if (this.drawerType === 'complete' || this.drawerType === 'remark') {
                const success = await this.$refs.completeOrRemark?.submit();
                if (success) {
                  this.showDrawer = false;
                }
              } else {
                this.$refs.createOrCopy?.submit();
              }
            },
          },
        ];
      },
      // 表格列配置
      tableColumns() {
        return [
          {
            label: '工单编码',
            prop: 'orderNo',
            minWidth: 140,
            render: (h, data, { row }) => {
              return (
                <ykc-button type="text" onClick={() => this.viewDetail(row)}>
                  {row.orderNo}
                </ykc-button>
              );
            },
          },
          { label: '故障等级', prop: 'faultLevelText', minWidth: 90 },
          { label: '标记状态', prop: 'markStatusText', minWidth: 90 },
          { label: '工单负责人', prop: 'assigneeName', minWidth: 100 },
          { label: '设备编码', prop: 'deviceNo', minWidth: 130 },
          { label: '设备品牌', prop: 'brand', minWidth: 100 },
          { label: '设备型号', prop: 'pileTypeText', minWidth: 120 },
          { label: '所属平台', prop: 'platformText', minWidth: 90 },
          { label: '电站名称', prop: 'stationName', minWidth: 150 },
          { label: '电站类型', prop: 'stationTypeText', minWidth: 90 },
          { label: '所属城市', prop: 'cityName', minWidth: 100 },
          { label: '故障类型', prop: 'faultCategory', minWidth: 100 },
          { label: '故障事项', prop: 'faultItem', minWidth: 120 },
          { label: '创建人', prop: 'creatorName', minWidth: 100 },
          { label: '创建时间', prop: 'createTime', minWidth: 140 },
          { label: '完成时间', prop: 'finishTime', minWidth: 140 },
          {
            label: '状态',
            prop: 'statusText',
            minWidth: 90,
            render: (h, data, { row }) => {
              const statusClass = this.getStatusClass(row.status);
              return <span class={statusClass}>{row.statusText || '-'}</span>;
            },
          },
        ];
      },
      // 操作按钮
      tableOperateButtons() {
        return [
          {
            enabled: () => code('operation:repairOrder:cancel'),
            render: (h, data, { row }) => {
              const status = Number(row.status);
              if (![10, 20].includes(status)) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleCancel(row)}>
                  作废
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:repairOrder:addRecord'),
            render: (h, data, { row }) => {
              const status = Number(row.status);
              if (![10, 20].includes(status)) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleAddRecord(row)}>
                  添加维修记录
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:repairOrder:complete'),
            render: (h, data, { row }) => {
              const status = Number(row.status);
              if (![10, 20].includes(status)) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleComplete(row)}>
                  完成
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:repairOrder:addRemark'),
            render: (h, data, { row }) => {
              const status = Number(row.status);
              if (status !== 40) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleAddRemark(row)}>
                  添加维修备注
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:repairOrder:copy'),
            render: (h, data, { row }) => {
              const status = Number(row.status);
              if (![40, 50].includes(status)) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleCopy(row)}>
                  复制
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:repairOrder:detail'),
            render: (h, data, { row }) => {
              return (
                <ykc-button type="text" onClick={() => this.viewDetail(row)}>
                  详情
                </ykc-button>
              );
            },
          },
        ];
      },
      confirmIconBg() {
        return '#fef3c7';
      },
      confirmIcon() {
        return 'fa-exclamation-triangle';
      },
      confirmIconColor() {
        return 'text-warning';
      },
    },
    watch: {
      'searchParams.platform': {
        handler() {
          // 切换平台时清空已选电站并重新加载
          this.searchParams.stationId = '';
          this.getStationData();
        },
      },
    },
    created() {
      this.getAddressData();
      this.getStationData();
    },
    mounted() {
      this.requestList();
    },
    methods: {
      // 获取城市数据
      getAddressData() {
        treeUtils
          .getDistrict({ level: 3 })
          .then(res => {
            this.addressData = res?.districts || [];
          })
          .catch(err => {
            console.error('获取城市数据失败:', err);
          });
      },
      // 获取电站数据（按平台加载淘淘充或益虫电站）
      getStationData() {
        const platform = this.searchParams.platform;
        if (platform === 2) {
          // 益虫平台
          repairOrderApi
            .getStationListYc({ pageNum: 1, pageSize: 500 })
            .then(res => {
              this.stationIdData = (res?.records || []).map(item => ({
                id: item.stationId,
                name: item.stationName,
              }));
            })
            .catch(err => {
              console.error('获取益虫电站列表失败:', err);
              this.stationIdData = [];
            });
        } else if (platform === 1) {
          // 淘淘充平台
          barrierPage
            .findStationList({})
            .then(res => {
              this.stationIdData = (res || []).map(item => ({
                id: item.stationId,
                name: item.stationName,
              }));
            })
            .catch(err => {
              console.error('获取电站列表失败:', err);
              this.stationIdData = [];
            });
        } else {
          // 全部：合并淘淘充和益虫电站
          Promise.all([
            barrierPage.findStationList({}).catch(() => []),
            repairOrderApi
              .getStationListYc({ pageNum: 1, pageSize: 500 })
              .catch(() => ({ records: [] })),
          ]).then(([ttcRes, ycRes]) => {
            const ttcStations = (ttcRes || []).map(item => ({
              id: item.stationId,
              name: item.stationName,
            }));
            const ycStations = (ycRes?.records || []).map(item => ({
              id: item.stationId,
              name: item.stationName,
            }));
            this.stationIdData = [...ttcStations, ...ycStations];
          });
        }
      },
      // 请求列表数据
      requestList() {
        const params = {
          ...this.searchParams,
          pageNum: this.pageInfo.current,
          pageSize: this.pageInfo.size,
        };
        // 处理城市级联（YkcCascader返回数组，取最后一级作为cityCode）
        if (Array.isArray(params.cityCode) && params.cityCode.length > 0) {
          params.cityCode = params.cityCode[params.cityCode.length - 1];
        } else if (Array.isArray(params.cityCode)) {
          delete params.cityCode;
        }
        // 处理时间（先删除展开的空字符串，再从数组赋值）
        delete params.createTime;
        delete params.createTimeStart;
        delete params.createTimeEnd;
        if (this.searchParams.createTime && this.searchParams.createTime.length === 2) {
          params.createTimeStart = this.searchParams.createTime[0];
          params.createTimeEnd = this.searchParams.createTime[1];
        }
        delete params.finishTime;
        delete params.finishTimeStart;
        delete params.finishTimeEnd;
        if (this.searchParams.finishTime && this.searchParams.finishTime.length === 2) {
          params.finishTimeStart = this.searchParams.finishTime[0];
          params.finishTimeEnd = this.searchParams.finishTime[1];
        }

        repairOrderApi
          .getList(params)
          .then(res => {
            this.tableData = res?.records || [];
            this.pageInfo.total = res?.total || 0;
          })
          .catch(err => {
            console.error('获取列表失败:', err);
          });
      },
      // 搜索确认
      handleSearchConfirm() {
        this.pageInfo.current = 1;
        this.requestList();
      },
      // 搜索清空
      handleSearchClean() {
        Object.assign(this.searchParams, {
          orderNo: '',
          assigneeName: '',
          deviceNo: '',
          stationId: '',
          cityCode: '',
          status: '',
          platform: '',
          creatorName: '',
          faultLevelList: [],
          markStatus: '',
          createTime: [],
          createTimeStart: '',
          createTimeEnd: '',
          finishTime: [],
          finishTimeStart: '',
          finishTimeEnd: '',
        });
        this.pageInfo.current = 1;
        this.requestList();
      },
      // 分页大小改变
      handlePaginationSizeChange(size) {
        this.pageInfo.size = size;
        this.requestList();
      },
      // 分页页码改变
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.requestList();
      },
      // 创建工单
      handleCreate() {
        this.drawerType = 'create';
        this.copyData = null;
        this.showDrawer = true;
      },
      // 关闭抽屉
      onDrawerClose(done) {
        done();
      },
      // 取消抽屉
      onDrawerCancel(drawerDone) {
        YkcDialog({
          dialogType: 'feedback',
          showTitle: false,
          showFooter: true,
          desc: '取消后操作将不被保存，确认是否取消？',
          onCancel: dialogDone => {
            dialogDone();
          },
          onConfirm: dialogDone => {
            dialogDone();
            drawerDone();
          },
        });
      },
      // 创建成功
      handleCreateSuccess() {
        this.showDrawer = false;
        this.requestList();
      },
      // 添加记录成功
      handleRecordSuccess() {
        this.showDrawer = false;
        this.requestList();
      },
      // 作废
      handleCancel(row) {
        this.confirmType = 'cancel';
        this.confirmTitle = '作废工单';
        this.confirmText = '确认作废该维修工单吗？作废后无法恢复。';
        this.confirmBtnTxt = '确认作废';
        this.currentOrderId = row.id;
        this.confirmVisible = true;
      },
      // 添加维修记录
      handleAddRecord(row) {
        this.currentOrder = row;
        this.batchOrderIds = null;
        this.drawerType = 'record';
        this.showDrawer = true;
      },
      // 完成工单
      handleComplete(row) {
        this.currentOrder = row;
        this.batchOrderIds = null;
        this.drawerType = 'complete';
        this.showDrawer = true;
      },
      // 添加维修备注
      handleAddRemark(row) {
        this.currentOrder = row;
        this.batchOrderIds = null;
        this.drawerType = 'remark';
        this.showDrawer = true;
      },
      // 确认提交
      async handleConfirmSubmit(done) {
        try {
          this.confirmLoading = true;
          if (this.confirmType === 'batchCancel') {
            await repairOrderApi.batchCancel({ orderIds: this.selectedIds });
            this.$message.success('批量作废成功');
          } else if (this.confirmType === 'batchMark') {
            await repairOrderApi.batchMark({
              orderIds: this.selectedIds,
              markStatus: this.batchMarkStatus,
            });
            this.$message.success(this.batchMarkStatus === 1 ? '批量标记成功' : '批量取消标记成功');
          } else {
            await repairOrderApi.cancel(this.currentOrderId);
            this.$message.success('工单已作废');
          }
          this.confirmVisible = false;
          this.requestList();
          done();
        } catch (error) {
          console.error('操作失败:', error);
          this.$message.error(error?.message || '操作失败');
        } finally {
          this.confirmLoading = false;
        }
      },
      // 关闭确认弹窗
      onConfirmClose(done) {
        done();
      },
      // 复制
      async handleCopy(row) {
        try {
          // 获取详情数据（列表行不含 faultImages 等字段）
          const detail = await repairOrderApi.getDetail(row.id);
          this.copyData = detail || row;
        } catch (error) {
          console.error('获取工单详情失败', error);
          this.copyData = row;
        }
        this.drawerType = 'copy';
        this.showDrawer = true;
      },
      // 查看详情
      viewDetail(row) {
        this.$router.push({
          path: '/operationManagement/repairOrder/detail',
          query: { id: row.id },
        });
      },
      // 导出
      doExportFile() {
        offlineExport(
          {
            downloadType: 'repair_order_export',
            jsonNode: {
              ...this.searchParams,
            },
          },
          this.pageInfo.total
        );
      },
      // 获取状态样式
      getStatusClass(status) {
        const statusMap = {
          10: 'status-pending',
          20: 'status-repairing',
          30: 'status-transferred',
          40: 'status-completed',
          50: 'status-cancelled',
        };
        return statusMap[status] || '';
      },
      // 选择变更
      handleSelectionChange(selection) {
        this.selectedRows = [...selection];
        this.selectedIds = selection.map(item => item.id);
      },
      // 批量作废
      handleBatchCancel() {
        this.confirmType = 'batchCancel';
        this.confirmTitle = '批量作废';
        this.confirmText = `确认作废选中的${this.selectedIds.length}条工单吗？仅待维修/维修中状态工单生效。`;
        this.confirmBtnTxt = '确认作废';
        this.confirmVisible = true;
      },
      // 批量添加维修记录
      handleBatchAddRecord() {
        this.currentOrder = null;
        this.batchOrderIds = [...this.selectedIds];
        this.drawerType = 'record';
        this.showDrawer = true;
      },
      // 批量完成
      handleBatchComplete() {
        this.currentOrder = null;
        this.batchOrderIds = [...this.selectedIds];
        this.drawerType = 'complete';
        this.showDrawer = true;
      },
      // 批量添加维修备注（需所选工单均为已完成状态）
      handleBatchAddRemark() {
        const nonCompleted = this.selectedRows.filter(row => row.status !== 40);
        if (nonCompleted.length > 0) {
          this.$message.warning('所选工单中存在非已完成状态，无法批量添加维修备注');
          return;
        }
        this.currentOrder = null;
        this.batchOrderIds = [...this.selectedIds];
        this.drawerType = 'remark';
        this.showDrawer = true;
      },
      // 批量标记/取消标记
      handleBatchMark(markStatus) {
        this.confirmType = 'batchMark';
        this.batchMarkStatus = markStatus;
        this.confirmTitle = markStatus === 1 ? '批量标记' : '批量取消标记';
        this.confirmText = `确认${markStatus === 1 ? '标记' : '取消标记'}选中的${
          this.selectedIds.length
        }条工单吗？`;
        this.confirmBtnTxt = '确认';
        this.confirmVisible = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .repair-order-list {
    .status-pending {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #fef3c7;
      color: #92400e;
    }

    .status-repairing {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #dbeafe;
      color: #1e40af;
    }

    .status-transferred {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #f3e8ff;
      color: #7c3aed;
    }

    .status-completed {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #d1fae5;
      color: #065f46;
    }

    .status-cancelled {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #fee2e2;
      color: #991b1b;
    }

    .text-warning {
      color: #f59e0b;
    }

    .text-success {
      color: #10b981;
    }

    .confirm-modal {
      text-align: center;
      padding: 20px 0;

      .confirm-icon {
        width: 48px;
        height: 48px;
        margin: 0 auto 16px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 20px;
        }
      }

      h3 {
        font-size: 16px;
        color: #1e293b;
        margin-bottom: 12px;
      }

      p {
        font-size: 14px;
        color: #64748b;
      }
    }
  }
</style>
